import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, finalize, take, takeUntil } from 'rxjs/operators';
import { CanActivateFn, Router } from '@angular/router';
import { AccountFeatures, AccountsService, IAccount, IUserPreferences, UserPreferences } from './accounts.service';
import { environment } from '@environments/environment';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { AuthService, User } from '@auth0/auth0-angular';
import { siteSubscriptions } from './siteSubscriptions';

/**
 * Interface for Web Application User
 */
export interface IWebAppUser {
	/** ID */
	UserID: number;
	/** UserGUID */
	UserGUID: string;
	/** Public ID from Auth0 (Sub) */
	Sub: any;
	/** First Name */
	FirstName: string;
	/** Last Name */
	LastName: string;
	/** User Nick Name */
	NickName: string;
	/** User Name */
	Name: string;
	/** Email */
	Email: string;
	/** Email */
	EmailVerified: boolean;
	/** Site Admin */
	AdminSite: boolean;
	/** System Admin */
	AdminSystems: boolean;
	/** Default Account */
	DefaultAccount: string;
	/** Disabled */
	Disabled: number;
	/** Picture */
	Picture: string;
	/** UpdatedAt */
	UpdatedAt: string;
	/** AccessLevel */
	AccessLevel: number;
	/** User Preferences */
	Preferences: string;
}

/**
 * Access Level Enum
 * @enum {number}
 * @description
 * This enum defines the various access levels for users in the web application. *
 */
export enum AccessLevel {
	Demo = 0,
	User = 10,
	Engineer = 20,
	AccountOwner = 30, "Account Owner" = 30,
	AccountBilling = 40, "Account Billing" = 40,
	AccountManager = 50, "Account Manager" = 50,
	AccountAdministrator = 60, "Account Administrator" = 60,
	SystemAdministrator = 999, "System Administrator" = 999
}

/**
 * Access Level Labels
 * @type {Record<AccessLevel, string>}
 * @description
 * This object maps each access level to its corresponding label for display purposes.
 * It provides a human-readable representation of each access level defined in the AccessLevel enum.
 *
 * @example
 * const accessLevel = AccessLevel.User;
 * const accessLevelLabel = AccessLevelLabels[accessLevel]; // "User"
 *
 *
 */
export const AccessLevelLabels: Record<AccessLevel, string> = {
	[AccessLevel.Demo]: "Demo",
	[AccessLevel.User]: "User",
	[AccessLevel.Engineer]: "Engineer",
	[AccessLevel.AccountOwner]: "Account Owner",
	[AccessLevel.AccountBilling]: "Account Billing",
	[AccessLevel.AccountManager]: "Account Manager",
	[AccessLevel.AccountAdministrator]: "Account Administrator",
	[AccessLevel.SystemAdministrator]: "System Administrator"
};

/**
 * resolve Access Level
 * @param value
 * @returns
 */
export function resolveAccessLevel(value: number): AccessLevel {
	const levels = Object.values(AccessLevel)
		.filter(v => typeof v === 'number') as number[];

	// Filter out levels that are greater than the input value
	const lowerLevels = levels.filter(level => level <= value);

	// If no lower level exists (value is below all defined levels), fallback to the lowest
	if (lowerLevels.length === 0) {
		return Math.min(...levels) as AccessLevel;
	}

	// Return the maximum of the lower/equal levels (i.e. the floor)
	const nearest = Math.max(...lowerLevels);
	return nearest as AccessLevel;
}

/**
 * Class Object for a Web Application User
 */
export class WebAppUser implements IWebAppUser {

	/**
	 * Web App User
	 * @param UserID
	 * @param UserGUID
	 * @param Sub
	 * @param FirstName
	 * @param LastName
	 * @param NickName
	 * @param Name
	 * @param Email
	 * @param EmailVerified
	 * @param AdminSite
	 * @param AdminSystems
	 * @param DefaultAccount
	 * @param Disabled
	 * @param Picture
	 * @param UpdatedAt
	 * @param AccessLevel
	 */
	constructor(
		UserID: number = 0,
		UserGUID: string = "",
		Sub: any = null,
		FirstName: string = '',
		LastName: string = '',
		NickName: string = '',
		Name: string = '',
		Email: string = '',
		EmailVerified: boolean = false,
		AdminSite: boolean = false,
		AdminSystems: boolean = false,
		DefaultAccount: string = '',
		Disabled: number = 0,
		Picture: string = '',
		UpdatedAt: string = '',
		AccessLevel: number = 0,
		Preferences: string = ""
	) {

		this.UserID = UserID
		this.UserGUID = UserGUID
		this.Sub = Sub
		this.FirstName = FirstName
		this.LastName = LastName
		this.NickName = NickName
		this.Name = Name
		this.Email = Email
		this.EmailVerified = EmailVerified
		this.AdminSite = AdminSite
		this.AdminSystems = AdminSystems
		this.DefaultAccount = DefaultAccount
		this.Disabled = Disabled
		this.Picture = Picture
		this.UpdatedAt = UpdatedAt
		this.AccessLevel = resolveAccessLevel(AccessLevel)
		this.Preferences = Preferences
	}

	/** ID */
	public UserID: number;
	/** UserGIID */
	public UserGUID: string;
	/** Public ID from Auth0 (Sub) */
	public Sub: any;
	/** First Name */
	public FirstName: string;
	/** Last Name */
	public LastName: string;
	/** User Nick Name */
	public NickName: string;
	/** User Name */
	public Name: string;
	/** Email */
	public Email: string;
	/** Email */
	public EmailVerified: boolean;
	/** Site Admin */
	public AdminSite: boolean;
	/** System Admin */
	public AdminSystems: boolean;
	/** Default Account */
	public DefaultAccount: string;
	/** Disabled */
	public Disabled: number;
	/** Picture */
	public Picture: string;
	/** UpdatedAt */
	public UpdatedAt: string;
	/** AccessLevel */
	public AccessLevel: number;
	/** User Preferences */
	public Preferences: string;
}


/**
 * AuthenticationService
 */
@Injectable({
	providedIn: 'root'
})

export class AuthenticationService {

	/** Current User */
	private currentUserSubject: BehaviorSubject<WebAppUser>;
	/** Current user */
	public currentUser$: Observable<WebAppUser>;

	/** Enable Console Messages */
	private Debug: boolean = false;

	/** Auth0 Organisation ID */
	private readonly Auth0OrgId: string = environment.auth.authorizationParams.organization;

	/**
	 * Log message to the Console
	 * @param str
	 */
	consoleLog(str: any) { if (this.Debug == true) console.log(str) }

	/** API Route */
	apiRoute: string = "api/users";

	/**
	 * Default Constructor
	 * @param http HTTP
	 * @param router Router
	 * @param accountsService
	 * @param globalFunctions Global Functions
	 * @param auth AuthService
	 */
	constructor(
		private http: HttpClient,
		private router: Router,
		private accountsService: AccountsService,
		public globalFunctions: GlobalFunctions,
		private auth: AuthService
	) {
		try {
			//this.currentUserSubject = new BehaviorSubject<any>(JSON.parse(localStorage.getItem("currentUser")!));
			this.currentUserSubject = new BehaviorSubject<any>(undefined); //(localStorage.getItem("currentUser")!);
			this.currentUser$ = this.currentUserSubject.asObservable();
		} catch (error) {
			console.error('An error occurred while initializing the current user:', error);
		}
	}

	/**
	 * Get All Users
	 * @param data An object containing query parameters for the GET request. Expected structure:
	 * {
	 *   filter?: string; // Optional filter string
	 *   sortBy?: string; // Optional sorting criteria
	 *   page?: number;   // Optional page number for pagination
	 *   pageSize?: number; // Optional page size for pagination
	 * }
	 * @returns An Observable emitting the list of users.
	 */
	getAllUsers$(data: { filter?: string; sortBy?: string; page?: number; pageSize?: number }): Observable<any> {
		let url = `${environment.apiUrl}/${this.apiRoute}`;
		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * Get Users
	 * @param data URL Parameters for Get Request
	 * @returns
	 */
	getUser$(id: string): Observable<any> {

		//let url = `${environment.apiUrl}/${this.apiRoute}/{user}`
		let url = `${environment.apiUrl}/${this.apiRoute}/${id}`;

		/** Fetch the data from API and Update the Observable subscriptions */
		return this.globalFunctions.fetchAPI(this.http, url, "GET", {});
	}

	/**
	 * Post Users
	 * @param data Body Data for Post Request
	 * @returns
	 */
	postUser$(data: any): Observable<any> {
		let url = `${environment.apiUrl}/${this.apiRoute}`;
		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * Create a new User Entry
	 * @param profile
	 */
	CreateUser(profile: User) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		let data = {
			"Name": profile['name'],
			"Email": profile['email'],
			"EmailVerified": profile['email_verified'],
			"NickName": profile['nickname'],
			"Sub": profile['sub'],
			"Picture": profile['picture'],
			"UpdatedAt": profile['updated_at']
		}

		this.postUser$(data).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((user) => !!user),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (user) => {
				// Set the Current User
				this.setCurrentUser(user);
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: this.authenticationService.postUser$", false);
				throw error; // Rethrow the error to propagate it
			},
			//complete: () => { console.log("this.authenticationService.postUser$.subscribe: Complete"); }
		});
	}

	/**
	 * Get Local prefrences settings
	 * @returns 
	 */
	getPreferences(user : IWebAppUser) : IUserPreferences {
		// let myPreferences = new UserPreferences()
		// try {

		// 	if (localStorage.getItem("myPrefrences")) {
		// 		myPreferences = JSON.parse(localStorage.getItem("myPrefrences") || "")
		// 	}
		// } catch (error) { console.error(error); }

		// return myPreferences
		return JSON.parse(user.Preferences ?? "{}") as IUserPreferences;
	}

	/**
	 * Set Current User
	 * @param thisUser
	 */
	setCurrentUser(thisUser: WebAppUser) {
		try {			
			this.currentUserSubject.next(thisUser);
		}
		catch (error) { console.error(error); }
	};

	/**
	 * Update Users
	 * @param id
	 * @param user
	 * @returns
	 */
	putUser$(id: string, user: IWebAppUser): Observable<any> {
		const data: string = JSON.stringify(user);
		let url = `${environment.apiUrl}/${this.apiRoute}/${id}`;
		return this.globalFunctions.fetchAPI(this.http, url, "PUT", data);
	};

	/**
	 * Patch User
	 * @param id
	 * @param user
	 * @returns
	 */
	patchUser$(id: string, user: IWebAppUser): Observable<any> {
		const data: string = JSON.stringify(user);
		let url = `${environment.apiUrl}/${this.apiRoute}/${id}`;
		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data);
	};

	/**
	 * Log in using the Organisation ID
	 * @param OrgID 
	 */
	login(OrgID: string = this.Auth0OrgId) {

		let params = {
			organization: OrgID,
		};

		var redirectionParams = {
			appState: { target: '/auth0login' },
			authorizationParams: params
		}

		this.auth.loginWithRedirect(redirectionParams)
	}

	/**
	 * Logout
	 * @param doc
	 */
	logout(doc?: Document, path?: string) {

		/** remove user from local storage and set current user to null */
		localStorage.removeItem('currentUser');
		localStorage.removeItem("myAccounts");
		// localStorage.removeItem("myOrganisations");

		this.currentUserSubject.next({} as WebAppUser);

		this.auth.logout({
			logoutParams: {
				returnTo: window.location.origin + '/logout'
			}
		});
	};

}

/**
 * Prevents users from accessing admin features as a non-admin.
 * @param route route 
 * @param state state 
 * @returns 
 */
export const AdminGuard: CanActivateFn = async (route, state) => {
	const authenticationService = inject(AuthenticationService);
	const router = inject(Router);

	const user = await firstValueFrom(
		authenticationService.currentUser$.pipe(
			filter(u => !!u),
			take(1)
		)
	);

	if (user.AdminSite || user.AccessLevel === 999) {
		return true;
	}

	router.navigate(['/unauthorised'], { state: { url: state.url } });
	return false;
}

/**
 * Prevents account users from accessing features for account admins
 * @param route route 
 * @param state state 
 * @returns 
 */
export const AccountAdminGuard: CanActivateFn = async (route, state) => {
	const authenticationService = inject(AuthenticationService);
	const _siteSubscriptions = inject(siteSubscriptions);
	const router = inject(Router);
	const user = await firstValueFrom(
		authenticationService.currentUser$.pipe(
			filter(u => !!u),
			take(1)
		)
	);
	let account = await firstValueFrom(
		_siteSubscriptions.selectedAccount$.pipe(
			// filter(a => !!a),
			take(1)
		)
	);

	if (user.AdminSite || user.AccessLevel === 999) {
		return true;
	} else if (!account || account.Admin) {
		return true;
	}

	router.navigate(['/unauthorised'], { state: { url: state.url } });
	return false;
}

/**
 * Prevents users from accessing features that their account does not have access to.
 * @param route route 
 * @param state state 
 * @returns 
 */
export const FeatureGuard: CanActivateFn = async (route, state) => {
	const authenticationService = inject(AuthenticationService);
	const accountsService = inject(AccountsService);
	const _siteSubscriptions = inject(siteSubscriptions);
	const router = inject(Router);
	const featureID = <AccountFeatures>route.data['featureID'];
	const accountGUID = route.params['account'];

	const user = await firstValueFrom(
		authenticationService.currentUser$.pipe(
			filter(u => !!u),
			take(1)
		)
	);

	let account = await firstValueFrom(_siteSubscriptions.selectedAccount$.pipe(take(1)));

	if (!account) {
		//get the account
		let accounts: IAccount[];
		let data = {
			"userPublicID": user.Sub,
			"showDisabled": false
		};

		try {
			accounts = await firstValueFrom(
				accountsService.getAccounts$(data).pipe(
					filter(u => !!u),
					take(1)
				)
			);
			accounts = _siteSubscriptions.normalizeFeatures(accounts) as IAccount[];
			if (accounts) {
				account = accounts.find((x: IAccount) => x.GUID === accountGUID) ?? {} as IAccount;
			}
		}
		catch (error) { }
	}

	if (user.AdminSite || user.AccessLevel === 999) {
		return true;
	} else {
		if (account) {
			let found = account.Features.find((x: any) => x.FeatureID == featureID && x.Enabled);
			if (found) { return true; }
		}
	}

	router.navigate(['/unauthorised'], { state: { url: state.url } });
	return false;
}
